import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  DataTable,
  Field,
  FormRow,
  Input,
  RightDrawer,
} from '@projexn/ui';
import { api } from '../lib/api';

interface Client {
  id: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  createdAt: string;
}

export function ClientsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const clientsQ = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ items: Client[] }>('/api/clients'),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Clients</h1>
          <p className="text-sm text-zinc-500">Live across all members on this tenant.</p>
        </div>
        <Button onClick={() => setCreating(true)}>New client</Button>
      </header>

      <Card className="overflow-hidden">
        <DataTable
          rows={clientsQ.data?.items ?? []}
          rowKey={(r) => r.id}
          columns={[
            { key: 'name', header: 'Name', render: (r) => r.name },
            { key: 'email', header: 'Email', render: (r) => r.contactEmail ?? '—' },
            { key: 'phone', header: 'Phone', render: (r) => r.contactPhone ?? '—' },
            {
              key: 'created',
              header: 'Created',
              render: (r) => new Date(r.createdAt).toLocaleDateString(),
            },
          ]}
          empty={<p className="px-6 py-12 text-center text-sm text-zinc-500">No clients yet.</p>}
        />
      </Card>

      <RightDrawer open={creating} onClose={() => setCreating(false)} title="New client">
        <NewClientForm
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['clients'] });
          }}
        />
      </RightDrawer>
    </div>
  );
}

function NewClientForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const mutate = useMutation({
    mutationFn: () =>
      api.post('/api/clients', {
        name,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
      }),
    onSuccess: onCreated,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutate.mutate();
      }}
      className="space-y-4"
    >
      <FormRow>
        <Field label="Name" required>
          <Input required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Email">
          <Input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
        </Field>
      </FormRow>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>
          Create
        </Button>
      </div>
    </form>
  );
}
